<script lang="ts">
	import type { HeroSlider, Post } from '#lib/schema-extension.js';
	import { Carousel, Controls } from 'flowbite-svelte';
	import { onMount } from 'svelte';
	import Placeholder from '#lib/components/Placeholder.svelte';
	import _ from 'lodash';
	import { directusReadItems } from '../directus.remote.js';
	import { PUBLIC_API_URL } from '$app/env/public';
	import { getId } from '../directus.js';
	import { setAttr } from '@directus/visual-editing';
	import { convertSlug } from '#lib/util.js';
	import { page } from '$app/state';

	let { block }: { block: HeroSlider } = $props();

	const posts = $derived(
		directusReadItems({
			collection: 'posts',
			query: {
				filter: {
					status: page.url.searchParams.has('preview')
						? { _in: ['published', 'draft'] }
						: { _in: ['published'] },
					slug: block.wildcats ? { _starts_with: 'wildcats/' } : { _nstarts_with: 'wildcats/' }
				},
				fields: ['*', { season: ['title'] }],
				sort: '-date_created',
				limit: 3
			}
		})
	);

	const mapImages = (posts: Post[]) => {
		return posts.map((p) => {
			const image = p.image;
			return {
				'data-post': p,
				alt: p.title,
				src: image ? `${PUBLIC_API_URL}/assets/${getId(image)}?key=slider` : ''
			};
		});
	};

	let index = $state(0);
	let paused = $state(false);
	let reducedMotion = $state(true);

	onMount(() => {
		const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
		const update = () => (reducedMotion = preference.matches);
		update();
		preference.addEventListener('change', update);
		return () => preference.removeEventListener('change', update);
	});
</script>

<div
	class="relative"
	data-directus={setAttr({
		collection: 'heroSlider',
		item: block.id!,
		mode: 'modal'
	})}
>
	{#await posts}
		<Placeholder text="Aktuelle Beiträge werden geladen" />
	{:then posts}
		{#if posts.length > 0}
			{@const activePost = posts[Math.min(index, posts.length - 1)]}
			<Carousel
				images={mapImages(posts)}
				bind:index
				slideDuration={reducedMotion ? 0 : 500}
				duration={block.autoplay && !paused && !reducedMotion && posts.length > 1
					? Math.max(5000, block.autoplayDuration ?? 5000)
					: 0}
				role="region"
				aria-roledescription="Karussell"
				aria-label="Aktuelle Beiträge"
				class="h-[clamp(15rem,45vw,30rem)]! rounded-none"
			>
				{#if posts.length > 1}
					<Controls>
						{#snippet children(changeSlide)}
							<button
								type="button"
								aria-label="Vorheriger Beitrag"
								class="btn btn-circle absolute top-1/2 left-3 z-30 -translate-y-1/2 border-0 bg-black/65 text-2xl text-white hover:bg-black/85"
								onclick={() => changeSlide(false)}>‹</button
							>
							<button
								type="button"
								aria-label="Nächster Beitrag"
								class="btn btn-circle absolute top-1/2 right-3 z-30 -translate-y-1/2 border-0 bg-black/65 text-2xl text-white hover:bg-black/85"
								onclick={() => changeSlide(true)}>›</button
							>
						{/snippet}
					</Controls>
				{/if}
			</Carousel>
			<div
				class="bg-base-100 flex flex-col gap-4 p-4 sm:p-6 md:flex-row md:items-center md:justify-between"
			>
				<div class="min-w-0 flex-1">
					<h2 class="border-0 font-bold">{_.upperFirst(activePost.title ?? '')}</h2>
					{#if activePost.description}
						<p class="mt-2 leading-relaxed">{_.upperFirst(activePost.description)}</p>
					{/if}
				</div>
				<div class="flex shrink-0 flex-wrap items-center gap-3">
					{#if block.autoplay && !reducedMotion && posts.length > 1}
						<button
							type="button"
							class="btn btn-ghost"
							aria-pressed={paused}
							onclick={() => (paused = !paused)}
						>
							{paused ? 'Diashow fortsetzen' : 'Diashow pausieren'}
						</button>
					{/if}
					<a href={convertSlug(activePost.slug, '/blog/{slug}')} class="btn btn-accent min-h-11"
						>Mehr lesen<span class="sr-only">: {activePost.title}</span></a
					>
				</div>
			</div>
		{/if}
	{:catch}
		<p class="bg-base-100 p-4" role="status">Aktuelle Beiträge konnten nicht geladen werden.</p>
	{/await}
</div>
